import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MetricsGrid from '../components/dashboard/MetricsGrid';
import FreshBoard from '../components/dashboard/FreshBoard';
import AlertsPanel from '../components/dashboard/AlertsPanel';
import QuickActions from '../components/dashboard/QuickActions';
import RevenueChart from '../components/dashboard/RevenueChart';
import InventoryOverview from '../components/dashboard/InventoryOverview';
import { REFRESH_INTERVAL_MS } from '../constants';
import SafeIcon from '../common/SafeIcon';
import { FiZap } from 'react-icons/fi';

const Dashboard = () => {
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // Stagger animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Page Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#e8f5e9] tracking-tight">
            Fresh Board <span className="text-[#84cc16]">Dashboard</span>
          </h1>
          <p className="text-[#6b8772] mt-1 font-mono text-sm">
            Real-time operations and sourcing intelligence
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-[#111916] border border-[#243129] rounded-lg px-4 py-2">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="w-2 h-2 bg-[#84cc16] rounded-full" />
                <div className="absolute inset-0 w-2 h-2 bg-[#84cc16] rounded-full animate-ping opacity-40" />
              </div>
              <span className="text-xs font-mono font-medium text-[#84cc16] uppercase tracking-wider">Live Data</span>
            </div>
          </div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="hidden md:flex items-center gap-2 bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] px-4 py-2 rounded-lg font-mono text-sm font-medium cursor-pointer shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-shadow"
          >
            <SafeIcon icon={FiZap} className="w-4 h-4" />
            <span>Quick Report</span>
          </motion.div>
        </div>
      </motion.div>

      {/* Metrics Grid */}
      <motion.div variants={itemVariants}>
        <MetricsGrid key={refreshKey} />
      </motion.div>

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fresh Board - Takes 2 columns on large screens */}
        <motion.div variants={itemVariants} className="lg:col-span-2 space-y-6">
          <FreshBoard />
          <RevenueChart />
        </motion.div>

        {/* Side Panel */}
        <motion.div variants={itemVariants} className="space-y-6">
          <AlertsPanel />
          <QuickActions />
          <InventoryOverview />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Dashboard;
